import { Box, Button, HStack, Table, Tbody, Text, Th, Thead, Tr, VStack } from '@elkaso-app/web-design';
import { useBulkUpsertDealsApi, useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import { EditableDealRowView } from './editable-deal-row';
import { useEditableDealsRows } from './use-editable-deals-rows';

interface Props {
  deals: any[];
  onDone: () => void; // exit edit mode
}

const HEADERS = [
  'Supplier',
  'Product',
  'UOM',
  'Brand',
  'Origin',
  'State',
  'Spec',
  'Category',
  'Deal Price',
  'Cashback %',
  'Active',
  '',
];

export const EditableDealsGrid = ({ deals, onDone }: Props) => {
  const { rows, errors, dirty, updateRow, insert, remove, append, prepend, buildPayload } = useEditableDealsRows(deals);
  const { mutate: bulkUpsert, isLoading, error: saveError } = useBulkUpsertDealsApi();
  // One shared fetch for every row's Category breadcrumb (the picker modal reuses the cache).
  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];

  const errorByClientId = new Map(errors.map((e) => [e.clientId, e.messages]));
  const serverErrors = saveError?.response?.data?.errors ?? [];

  const onSave = () => {
    if (errors.length > 0) return; // button is disabled, but guard anyway
    // Only created/edited rows are emitted; nothing changed → no request, just exit edit mode.
    const changedRows = buildPayload();
    if (changedRows.length === 0) return onDone();
    bulkUpsert({ rows: changedRows }, { onSuccess: () => onDone() });
  };

  const onCancel = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onDone();
  };

  return (
    <VStack align='stretch' spacing='md'>
      <HStack justify='space-between' py='sm' px='xs'>
        <HStack spacing='md'>
          <Button size='sm' variant='outline' onClick={prepend}>
            Add row to top
          </Button>
          <Button size='sm' variant='outline' onClick={append}>
            Add row to bottom
          </Button>
        </HStack>
        <HStack spacing='sm'>
          <Button size='sm' variant='ghost' colorScheme='gray' onClick={onCancel}>
            Cancel
          </Button>
          <Button size='sm' colorScheme='red' isLoading={isLoading} isDisabled={errors.length > 0} onClick={onSave}>
            Save
          </Button>
        </HStack>
      </HStack>

      {errors.length > 0 && (
        <Box bg='red.50' borderRadius='md' p='sm'>
          <Text fontSize='sm' color='red.600' fontWeight='semibold'>
            Fix {errors.length} row(s) before saving:
          </Text>
          {errors.map((e) => (
            <Text key={e.clientId} fontSize='sm' color='red.600'>
              Row {e.rowIndex + 1}: {e.messages.join(', ')}
            </Text>
          ))}
        </Box>
      )}

      {serverErrors.length > 0 && (
        <Box bg='red.50' borderRadius='md' p='sm'>
          <Text fontSize='sm' color='red.600' fontWeight='semibold'>
            Save failed — fix these and try again:
          </Text>
          {serverErrors.map((msg, i) => (
            <Text key={i} fontSize='sm' color='red.600'>
              {msg}
            </Text>
          ))}
        </Box>
      )}

      <Box overflowX='auto' overflowY='auto' maxH='65vh' border='1px solid' borderColor='gray.200' borderRadius='md'>
        <Table
          size='sm'
          sx={{
            // Excel-like density: tight cells, gridlines on every cell, sticky compact header
            'th, td': { borderWidth: '1px', borderColor: 'gray.200', px: '1.5', py: '1.5', verticalAlign: 'middle' },
            th: {
              bg: 'gray.50',
              textTransform: 'none',
              fontSize: 'xs',
              letterSpacing: 'normal',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              whiteSpace: 'nowrap',
            },
            // Subtle row-hover highlight for scannability. Semi-transparent so it layers over the
            // red invalid-row background instead of masking it.
            'tbody tr:hover td': { bg: 'blackAlpha.50' },
          }}>
          <Thead>
            <Tr>
              {HEADERS.map((h, i) => (
                <Th key={i}>{h}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, index) => (
              <EditableDealRowView
                key={row.clientId}
                row={row}
                index={index}
                invalid={errorByClientId.has(row.clientId)}
                categoryTree={categoryTree}
                onChange={updateRow}
                onInsert={insert}
                onRemove={remove}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};
